import type { PermisoCatalogo, PermisoModulo } from '../types/permisos';
import { pathToPermissionKey } from '../utils/permisoKeys';

export const EMPTY_PERMISO_CATALOGO: PermisoCatalogo = { modulos: [] };

export type TipoAjusteInventario = 'positivo' | 'negativo' | 'transferencia';

export const PERMISO_AJUSTE_INVENTARIO: Record<TipoAjusteInventario, string> = {
  positivo: 'inventario.transferencias.ajustepositivo',
  negativo: 'inventario.transferencias.ajustenegativo',
  transferencia: 'inventario.transferencias.nuevatransferencia',
};

export function permisoAjusteInventario(tipo: TipoAjusteInventario): string {
  return PERMISO_AJUSTE_INVENTARIO[tipo];
}

function normalizePath(pathname: string): string {
  const path = (pathname.split('?')[0] || '/').replace(/\/+$/, '');
  return path || '/';
}

export function collectCatalogKeys(catalogo: PermisoCatalogo): string[] {
  const keys: string[] = [];
  catalogo.modulos.forEach((mod) => {
    keys.push(mod.key);
    mod.paginas.forEach((page) => {
      keys.push(page.key);
      page.controles.forEach((control) => keys.push(control.key));
    });
  });
  return Array.from(new Set(keys));
}

export function collectKnownPageKeys(catalogo: PermisoCatalogo): Set<string> {
  const keys = new Set<string>();
  catalogo.modulos.forEach((mod) => {
    keys.add(mod.key);
    mod.paginas.forEach((page) => keys.add(page.key));
  });
  return keys;
}

export function descendantsOfKey(key: string, catalogo: PermisoCatalogo): string[] {
  const found: string[] = [];
  const prefix = `${key}.`;
  catalogo.modulos.forEach((mod) => {
    if (mod.key === key) {
      mod.paginas.forEach((page) => {
        found.push(page.key);
        page.controles.forEach((control) => found.push(control.key));
      });
      return;
    }
    mod.paginas.forEach((page) => {
      if (page.key === key || page.key.startsWith(prefix)) {
        if (page.key !== key) found.push(page.key);
        page.controles.forEach((control) => found.push(control.key));
      }
    });
  });
  return Array.from(new Set(found));
}

export function findModuloInCatalogo(catalogo: PermisoCatalogo, pathOrKey: string): PermisoModulo | undefined {
  const key = pathOrKey.startsWith('/') ? pathToPermissionKey(pathOrKey) : pathOrKey;
  const path = pathOrKey.startsWith('/') ? normalizePath(pathOrKey) : '';
  return catalogo.modulos.find((mod) => mod.key === key || mod.key === pathOrKey || (path && mod.path === path));
}

/** Resuelve la clave de página/módulo del catálogo para un path (incluye rutas hijas: /facturas/nuevo → facturas). */
export function resolvePagePermissionKey(pathname: string, catalogo: PermisoCatalogo): string | null {
  const path = normalizePath(pathname);
  const byPath = new Map<string, string>();
  catalogo.modulos.forEach((mod) => {
    if (mod.path) byPath.set(normalizePath(mod.path), mod.key);
    mod.paginas.forEach((page) => {
      if (page.path) byPath.set(normalizePath(page.path), page.key);
    });
  });

  // Evita que /inventario/fashion* herede el módulo inventario cuando Fashion está desactivado.
  const logicalRoot = pathToPermissionKey(path).split('.')[0];
  const sameModule = (catalogKey: string) => catalogKey.split('.')[0] === logicalRoot;

  const segments = path.split('/').filter(Boolean);
  for (let i = segments.length; i >= 1; i -= 1) {
    const candidate = `/${segments.slice(0, i).join('/')}`;
    const fromPath = byPath.get(candidate);
    if (fromPath && sameModule(fromPath)) return fromPath;
  }

  const knownKeys = collectKnownPageKeys(catalogo);
  const exact = pathToPermissionKey(path);
  if (knownKeys.has(exact)) return exact;
  for (let i = segments.length - 1; i >= 1; i -= 1) {
    const key = pathToPermissionKey(`/${segments.slice(0, i).join('/')}`);
    if (knownKeys.has(key) && sameModule(key)) return key;
  }
  return null;
}
